/**
 * User Service
 *
 * Service layer for user API operations
 * Provides clean abstraction for user-related API calls
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';
import type {
  UserResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserListParams,
  PageUserResponse,
} from '../types/user.types';

const USER_ENDPOINTS = {
  USERS: '/api/users',
  USER_BY_ID: (id: string) => `/api/users/${id}`,
  OTHER_USERS: '/api/users/lists-other-user',
} as const;

class UserService {
  async getUsers(params: UserListParams = {}): Promise<PageUserResponse> {
    const { page = 0, size = 20, sort } = params;

    return apiGet<PageUserResponse>(USER_ENDPOINTS.USERS, {
      params: {
        page,
        size,
        ...(sort && sort.length > 0 && { sort }),
      },
    });
  }

  async getUserById(id: string): Promise<UserResponse> {
    if (!id) {
      throw new Error('User ID is required');
    }
    return apiGet<UserResponse>(USER_ENDPOINTS.USER_BY_ID(id));
  }

  async createUser(data: UserCreateRequest): Promise<UserResponse> {
    return apiPost<UserResponse>(USER_ENDPOINTS.USERS, data);
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<UserResponse> {
    if (!id) {
      throw new Error('User ID is required');
    }
    return apiPut<UserResponse>(USER_ENDPOINTS.USER_BY_ID(id), data);
  }

  async deleteUser(id: string): Promise<void> {
    if (!id) {
      throw new Error('User ID is required');
    }
    return apiDelete<void>(USER_ENDPOINTS.USER_BY_ID(id));
  }

  async getOtherUsers(): Promise<UserResponse[]> {
    return apiGet<UserResponse[]>(USER_ENDPOINTS.OTHER_USERS);
  }
}

// Export singleton instance
export const userService = new UserService();
